import { authorshipToolLabel } from "@afar-research/afar";
import type { ContributionsModelLines } from "@afar-research/contracts";

export interface AuthorshipShares {
  readonly totalLines: number;
  readonly ai: number;
  readonly human: number;
  readonly unknown: number;
}

/**
 * Whole percentages of added lines by author that always add up to 100, by
 * largest remainder. A share that exists is never shown as 0%, so one agent
 * line in a thousand still reads as a contribution.
 */
export function authorshipShares(lines: {
  readonly aiLines: number;
  readonly humanLines: number;
  readonly unknownLines: number;
}): AuthorshipShares {
  const totalLines = lines.aiLines + lines.humanLines + lines.unknownLines;
  if (totalLines === 0) return { totalLines, ai: 0, human: 0, unknown: 0 };
  const parts = [lines.aiLines, lines.humanLines, lines.unknownLines].map((count) => {
    const exact = (count / totalLines) * 100;
    const floor = count > 0 ? Math.max(1, Math.floor(exact)) : 0;
    return { count, floor, remainder: exact - Math.floor(exact) };
  });
  let missing = 100 - parts.reduce((sum, part) => sum + part.floor, 0);
  const byRemainder = parts
    .map((part, index) => ({ ...part, index }))
    .filter((part) => part.count > 0)
    .toSorted((left, right) => right.remainder - left.remainder);
  const shares = parts.map((part) => part.floor);
  // Each step moves one point, and at most a few points are ever off; the cap
  // only guards against a loop that cannot settle.
  for (let step = 0; missing !== 0 && byRemainder.length > 0 && step < 100; step += 1) {
    const target = byRemainder[step % byRemainder.length]!;
    if (missing > 0) {
      shares[target.index]! += 1;
      missing -= 1;
    } else if (shares[target.index]! > 1) {
      shares[target.index]! -= 1;
      missing += 1;
    }
  }
  return { totalLines, ai: shares[0]!, human: shares[1]!, unknown: shares[2]! };
}

export function contributionModelLabel(entry: Pick<ContributionsModelLines, "tool" | "model">) {
  return `${authorshipToolLabel(entry.tool)} · ${entry.model}`;
}
